import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Clock,
    Loader2,
    LogOut,
    Youtube,
    MessageCircle,
    Instagram,
    Send as Telegram,
    Facebook,
    ExternalLink,
    CheckCircle2,
    Users,
    Heart,
    Sparkles,
    UserX,
    Shield,
    XCircle
} from 'lucide-react';

export default function PendingApproval() {
    const { logout, userData, isRejected } = useAuth(); // Modified
    const [socialLinks, setSocialLinks] = useState({});
    const [loading, setLoading] = useState(true);
    const [loggingOut, setLoggingOut] = useState(false);

    const socialPlatforms = [
        { key: 'youtube', label: 'YouTube', icon: Youtube, color: 'bg-red-500 hover:bg-red-600' },
        { key: 'facebook', label: 'Facebook', icon: Facebook, color: 'bg-blue-600 hover:bg-blue-700' },
        { key: 'instagram', label: 'Instagram', icon: Instagram, color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600' },
        { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-green-500 hover:bg-green-600' },
        { key: 'telegram', label: 'Telegram', icon: Telegram, color: 'bg-blue-400 hover:bg-blue-500' }
    ];

    useEffect(() => {
        fetchSocialLinks();
    }, []);

    const fetchSocialLinks = async () => {
        try {
            const settingsDoc = await getDoc(doc(db, 'settings', 'social_links'));
            if (settingsDoc.exists()) {
                setSocialLinks(settingsDoc.data());
            }
        } catch (error) {
            console.error('Error fetching social links:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await logout();
        } catch (error) {
            console.error('Error logging out:', error);
        } finally {
            setLoggingOut(false);
        }
    };

    const availableLinks = socialPlatforms.filter(platform => socialLinks[platform.key]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
            {/* Background pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiIGN4PSIyMCIgY3k9IjIwIiByPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />

            {/* Decorative blurs */}
            <div className={`absolute top-20 left-20 w-72 h-72 rounded-full blur-[100px] animate-pulse ${isRejected ? 'bg-red-500/20' : 'bg-primary/30'}`} /> {/* Modified */}
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[120px] animate-pulse delay-1000" />

            <div className="relative z-10 w-full max-w-2xl">
                {/* Main Card */}
                <Card className="bg-white/10 backdrop-blur-xl border-white/20 rounded-[2.5rem] shadow-2xl overflow-hidden">
                    <CardContent className="p-8 md:p-12">
                        {/* Logo and Welcome */}
                        <div className="text-center mb-8">
                            <div className="relative inline-block mb-6">
                                <div className="w-24 h-24 rounded-[2rem] bg-white flex items-center justify-center overflow-hidden border-4 border-primary/50 shadow-2xl mx-auto">
                                    <img src="/logo.jpg" alt="Logo" className="w-full h-full object-cover" />
                                </div>
                                <div className={`absolute -bottom-2 -right-2 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${isRejected ? 'bg-red-500' : 'bg-amber-500'}`}> {/* Modified */}
                                    {isRejected ? <UserX className="w-5 h-5 text-white" /> : <Clock className="w-5 h-5 text-white animate-pulse" />} {/* Modified */}
                                </div>
                            </div>

                            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-3">
                                {isRejected ? 'Accès refusé' : `Bienvenue, ${userData?.fullName?.split(' ')[0] || 'Membre'} !`} {/* Modified */}
                            </h1>

                            {isRejected ? ( // Modified
                                <p className="text-red-400 text-lg max-w-md mx-auto leading-relaxed font-semibold">
                                    Votre demande d'adhésion n'a pas été approuvée par l'administration.
                                </p>
                            ) : (
                                <p className="text-slate-300 text-lg max-w-md mx-auto leading-relaxed">
                                    Votre inscription a bien été enregistrée.
                                    <span className="text-amber-400 font-semibold"> En attente de validation</span> par l'administration.
                                </p>
                            )}
                        </div>

                        {/* Status Card */}
                        <div className={`bg-white/5 backdrop-blur-sm rounded-2xl p-6 border mb-8 ${isRejected ? 'border-red-500/20' : 'border-white/10'}`}> {/* Modified */}
                            <div className="flex items-start gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isRejected ? 'bg-red-500/20' : 'bg-amber-500/20'}`}> {/* Modified */}
                                    {isRejected ? <Shield className="w-6 h-6 text-red-400" /> : <Sparkles className="w-6 h-6 text-amber-400" />} {/* Modified */}
                                </div>
                                <div>
                                    <h3 className="text-white font-bold text-lg mb-1">{isRejected ? 'Informations' : 'Prochaines étapes'}</h3> {/* Modified */}
                                    <ul className="text-slate-400 space-y-2 text-sm">
                                        {isRejected ? ( // Modified
                                            <>
                                                <li className="flex items-center gap-2">
                                                    <XCircle className="w-4 h-4 text-red-400" />
                                                    <span>Votre compte a été désactivé par un modérateur</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <MessageCircle className="w-4 h-4 text-emerald-400" />
                                                    <span>Veuillez contacter le bureau du Dahira pour plus de précisions</span>
                                                </li>
                                            </>
                                        ) : (
                                            <>
                                                <li className="flex items-center gap-2">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                                    <span>Votre demande est en cours de traitement</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <Users className="w-4 h-4 text-blue-400" />
                                                    <span>Un administrateur validera votre compte sous peu</span>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <Heart className="w-4 h-4 text-pink-400" />
                                                    <span>Vous recevrez une notification une fois approuvé</span>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Social Links Section */}
                        {availableLinks.length > 0 && (
                            <div className="mb-8">
                                <h3 className="text-white font-bold text-lg text-center mb-4">
                                    En attendant, rejoignez notre communauté !
                                </h3>
                                <p className="text-slate-400 text-center text-sm mb-6">
                                    Restez connecté avec le Dahira sur nos réseaux sociaux
                                </p>

                                {loading ? (
                                    <div className="flex justify-center py-6">
                                        <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {availableLinks.map((platform) => (
                                            <a
                                                key={platform.key}
                                                href={socialLinks[platform.key]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-white font-bold transition-all transform hover:scale-105 hover:shadow-xl ${platform.color}`}
                                            >
                                                <platform.icon className="w-5 h-5" />
                                                <span>{platform.label}</span>
                                                <ExternalLink className="w-4 h-4 opacity-70" />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Logout Button */}
                        <div className="text-center">
                            <Button
                                variant="ghost"
                                onClick={handleLogout}
                                disabled={loggingOut}
                                className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl px-6"
                            >
                                {loggingOut ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Déconnexion...
                                    </>
                                ) : (
                                    <>
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Se déconnecter
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Footer Info */}
                <p className="text-center text-slate-500 text-sm mt-6">
                    Des questions ? Contactez-nous via nos réseaux sociaux.
                </p>
            </div>
        </div>
    );
}
